import { Injectable, inject } from '@angular/core';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationService, NotificationServiceInjector } from '@next/core-lib/notification';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { merge, of } from 'rxjs';
import { map, filter, concatMap, withLatestFrom } from 'rxjs/operators';

import { SiteState } from 'src/app/shared/models';

import { GetCropFieldAdvices } from 'src/app/store/actions';

import * as CopyAdviceActions from './copy-advice.actions';
import * as CopyAdviceSelectors from './copy-advice.selectors';
import { CopyAdviceProgressDialogComponent } from '../components/copy-advice-progress-dialog/copy-advice-progress-dialog.component';
import {
  CopyTreatmentsStarted,
  CompletedCopyTreatments,
  CopyAdviceEvents,
} from '../copy-advice.models';

/**
 * Set of effects to show a nice loading state for copy actions that can take a while.
 * - Listens to a start event emitted via SignalR
 * - Shows a progress bar based on the amount of advices to copy as sent via SignalR
 * - Updates the progress bar when events start completing
 * - Hides the progress bar when the queue is emptied and all copy actions are done
 *
 * This flow is used for both CopyTreatments and CopyByTreatments.
 */
@Injectable()
export class CopyAdviceEffects {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly notification = inject<NotificationService>(NotificationServiceInjector);

  copyTreatmentsStartedEvent$ = createEffect(() =>
    merge(
      this.notification.on<CopyTreatmentsStarted>(CopyAdviceEvents.CopyTreatmentsStarted),
      this.notification.on<CopyTreatmentsStarted>(CopyAdviceEvents.CopyByTreatmentsStarted),
      this.notification.on<CopyTreatmentsStarted>(CopyAdviceEvents.CopyExecutedTreatmentsStarted),
    ).pipe(
      map(({ amountOfAdvices }) => CopyAdviceActions.copyTreatmentsStarted({ amountOfAdvices })),
    ),
  );

  completedCopyTreatmentsEvent$ = createEffect(() =>
    merge(
      this.notification.on<CompletedCopyTreatments>(CopyAdviceEvents.CompletedCopyTreatments),
      this.notification.on<CompletedCopyTreatments>(CopyAdviceEvents.CompletedCopyByTreatments),
      this.notification.on<CompletedCopyTreatments>(
        CopyAdviceEvents.CompletedCopyExecutedTreatments,
      ),
    ).pipe(
      map(({ isSuccessful, message }) =>
        CopyAdviceActions.completedCopyTreatments({ isSuccessful, message }),
      ),
    ),
  );

  copyTreatmentsStarted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopyAdviceActions.copyTreatmentsStarted),
      concatMap((_) =>
        this.dialog.dialog(
          CopyAdviceProgressDialogComponent,
          { title: this.locale.translate('copyTitle') },
          { autoFocus: false },
        ),
      ),
      withLatestFrom(this.store.pipe(select(CopyAdviceSelectors.isCopying))),
      filter(([_, isCopying]) => isCopying),
      map((_) => CopyAdviceActions.copyDialogDismissed()),
    ),
  );

  completedCopyTreatments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopyAdviceActions.completedCopyTreatments),
      withLatestFrom(this.store.pipe(select(CopyAdviceSelectors.isCopying))),
      filter(([_, isCopying]) => !isCopying),
      map((_) => CopyAdviceActions.completedCopyAll()),
    ),
  );

  completedCopyAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopyAdviceActions.completedCopyAll),
      concatMap(() => of(new GetCropFieldAdvices())),
    ),
  );

  completedCopyTreatmentsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CopyAdviceActions.completedCopyTreatmentsError),
        concatMap(() =>
          this.dialog.notify({
            title: 'copyAdvice.errorTitle',
            text: this.locale.translate('copyAdvice.errorMessage'),
          }),
        ),
      ),
    { dispatch: false },
  );
}
